import { useMemo, useEffect } from "react";

import { motion, useSpring, useTransform } from "framer-motion";
import { Building2, Plus, User } from "lucide-react";

import { cn } from "lib/utils";

import type { Advisor, Credits } from "js/pages/Organization/types";

type QuoteType = "PF" | "PM";

interface BalanceHeroProps {
  credits: Credits;
  advisors: Advisor[];
  isLoading: boolean;
  isOwner?: boolean;
  onRecharge?: (type: QuoteType) => void;
}

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as const },
  },
};

const THEMES: Record<
  QuoteType,
  {
    bg: string;
    border: string;
    dot: string;
    badge: string;
    badgeText: string;
    buttonBg: string;
    buttonHover: string;
    iconBg: string;
    iconColor: string;
    icon: typeof User;
  }
> = {
  PF: {
    bg: "bg-gradient-to-br from-indigo-50 to-white/60",
    border: "border-indigo-200",
    dot: "bg-indigo-500",
    badge: "bg-indigo-100/80",
    badgeText: "text-indigo-700",
    buttonBg: "bg-indigo-600",
    buttonHover: "hover:bg-indigo-700",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    icon: User,
  },
  PM: {
    bg: "bg-gradient-to-br from-violet-50 to-white/60",
    border: "border-violet-200",
    dot: "bg-violet-500",
    badge: "bg-violet-100/80",
    badgeText: "text-violet-700",
    buttonBg: "bg-violet-600",
    buttonHover: "hover:bg-violet-700",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    icon: Building2,
  },
};

// Track globally whether the initial animation has played (survives remounts)
let hasPlayedInitialAnimation = false;

function AnimatedNumber({ value }: { value: number }) {
  const startFrom = hasPlayedInitialAnimation ? value : 0;
  const spring = useSpring(startFrom, { stiffness: 50, damping: 20 });
  const display = useTransform(spring, (v) =>
    Math.round(v).toLocaleString("es-MX"),
  );

  useEffect(() => {
    spring.set(value);
    hasPlayedInitialAnimation = true;
  }, [spring, value]);

  return <motion.span>{display}</motion.span>;
}

function CreditCard({
  total,
  assigned,
  label,
  type,
  isOwner,
  onRecharge,
}: {
  total: number;
  assigned: number;
  label: string;
  type: QuoteType;
  isOwner?: boolean;
  onRecharge?: () => void;
}) {
  const theme = THEMES[type];

  return (
    <motion.div
      variants={cardVariants}
      className={cn(
        "relative flex flex-col justify-between overflow-hidden rounded-2xl border p-6 xl:min-w-[280px] xl:max-w-sm xl:flex-1",
        theme.bg,
        theme.border,
      )}
    >
      {/* Label */}
      <div className="flex items-center gap-3">
        <theme.icon className={cn("size-6 shrink-0", theme.iconColor)} />
        <div className="flex flex-col">
          <span className="text-base font-semibold text-foreground">
            {label}
          </span>
          <span className="text-xs text-muted-foreground">
            Créditos disponibles
          </span>
        </div>
      </div>

      {/* Balance */}
      <div className="mt-4">
        <div className="text-4xl font-semibold tracking-tight tabular-nums text-foreground">
          <AnimatedNumber value={total} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums",
            theme.badge,
            theme.badgeText,
          )}
        >
          {assigned.toLocaleString("es-MX")} asignados
        </span>

        {isOwner && onRecharge && (
          <button
            type="button"
            onClick={onRecharge}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-medium text-white shadow-sm transition-all active:scale-[0.97]",
              theme.buttonBg,
              theme.buttonHover,
            )}
          >
            <Plus className="size-3.5" />
            Recargar
          </button>
        )}
      </div>
    </motion.div>
  );
}

function BalanceHeroSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:flex xl:flex-wrap">
      {[
        { bg: "bg-gradient-to-br from-indigo-50 to-white/60", border: "border-indigo-200" },
        { bg: "bg-gradient-to-br from-violet-50 to-white/60", border: "border-violet-200" },
      ].map((t, i) => (
        <div
          key={i}
          className={cn(
            "rounded-2xl border p-6 xl:min-w-[280px] xl:max-w-sm xl:flex-1",
            t.bg,
            t.border,
          )}
        >
          <div className="flex items-center gap-2">
            <div className="size-2 animate-pulse rounded-full bg-muted/60" />
            <div className="h-3 w-24 animate-pulse rounded bg-muted/60" />
          </div>
          <div className="mt-4 h-10 w-28 animate-pulse rounded bg-muted/60" />
          <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-muted/60" />
          <div className="mt-6 flex items-center justify-between">
            <div className="h-5 w-20 animate-pulse rounded-full bg-muted/60" />
            <div className="h-8 w-24 animate-pulse rounded-lg bg-muted/60" />
          </div>
        </div>
      ))}
    </div>
  );
}

function BalanceHero({
  credits,
  advisors,
  isLoading,
  isOwner,
  onRecharge,
}: BalanceHeroProps) {
  const assignedPf = useMemo(
    () => advisors.reduce((sum, a) => sum + (a.credits?.pf ?? 0), 0),
    [advisors],
  );
  const assignedPm = useMemo(
    () => advisors.reduce((sum, a) => sum + (a.credits?.pm ?? 0), 0),
    [advisors],
  );

  if (isLoading) return <BalanceHeroSkeleton />;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:flex xl:flex-wrap"
    >
      <CreditCard
        total={credits.pf ?? 0}
        assigned={assignedPf}
        label="Persona Física"
        type="PF"
        isOwner={isOwner}
        onRecharge={() => onRecharge?.("PF")}
      />
      <CreditCard
        total={credits.pm ?? 0}
        assigned={assignedPm}
        label="Persona Moral"
        type="PM"
        isOwner={isOwner}
        onRecharge={() => onRecharge?.("PM")}
      />
    </motion.div>
  );
}

export default BalanceHero;
